import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { noteService } from '../services/noteService';

const NotePage = () => {
  const { folderId, noteId } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    loadNote();
  }, [folderId, noteId]);

  const loadNote = async () => {
    try {
      const response = await noteService.getNote(folderId, noteId);
      setNote(response.data);
    } catch (error) {
      console.error('Error loading note:', error);
    }
  };

  const handleEdit = async () => {
    if (isEditing) {
      try {
        await noteService.updateNote(folderId, noteId, note);
        loadNote();
      } catch (error) {
        console.error('Error updating note:', error);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await noteService.deleteNote(folderId, noteId);
        navigate(`/notes/folder/${folderId}`);
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
  };

  const handleContentChange = (event) => {
    setNote({
      ...note,
      content: event.target.value
    });
  };

  const handleTitleChange = (event) => {
    setNote({
      ...note,
      title: event.target.value
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: '800px', margin: '0 auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          {isEditing ? (
            <TextField
              value={note?.title || ''}
              onChange={handleTitleChange}
              variant="outlined"
              fullWidth
              placeholder="Note Title"
              sx={{ fontFamily: 'monospace' }}
            />
          ) : (
            <Typography variant="h5" component="h1">
              {note?.title || 'Untitled Note'}
            </Typography>
          )}
          <Box>
            <IconButton onClick={handleEdit}>
              {isEditing ? <SaveIcon /> : <EditIcon />}
            </IconButton>
            <IconButton onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Stack>
        
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Folder: {note?.folder} | Last Modified: {note?.lastModified}
        </Typography>
        
        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <TextField
            multiline
            fullWidth
            minRows={15}
            value={note?.content}
            onChange={handleContentChange}
            variant="outlined"
            sx={{ fontFamily: 'monospace' }}
          />
        ) : (
          <Box sx={{ mt: 2 }}>
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: props => <Typography variant="h4" gutterBottom {...props} />,
                h2: props => <Typography variant="h5" gutterBottom {...props} />,
                h3: props => <Typography variant="h6" gutterBottom {...props} />,
                p: props => <Typography paragraph {...props} />,
                pre: props => (
                  <Paper sx={{ p: 2, my: 2, bgcolor: 'grey.100' }}>
                    <pre style={{ margin: 0 }} {...props} />
                  </Paper>
                ),
              }}
            >
              {note?.content}
            </ReactMarkdown>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default NotePage;
